import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import type { TTableCell as Cell } from '@chocolate-soup-inc/cs-frontend-components';
import { Loading, LoadingPage, tableClassNames, TableInner, TOnRowClick } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import { quickScore } from 'quick-score';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage } from '../../components/tablePage/TablePage';
import tableStyles from '../../components/tablePage/TablePage.module.scss';
import { useFilter } from '../../contexts/filters';
import { useQueryListAllCompaniesOLD } from '../../entities/companies/queries';
import { TListAllCompaniesFieldsFragment } from '../../generated/graphql';
import { COMPANY_PATH } from '../../routes/paths';

enum TCompanyStatuses {
  Active = 'active',
  Inactive = 'inactive',
  Canceled = 'canceled',
  Deleted = 'deleted'
}

const ConfiguredCell = (props: { cell: Cell<TListAllCompaniesFieldsFragment, unknown> }) => {
  const { cell } = props;

  const fullyConfigured = useMemo(() => {
    return cell.row.original.fullyConfigured;
  }, [cell.row.original.fullyConfigured]);

  if (fullyConfigured)
    return (
      <div className={clsx(tableClassNames.tertiary)}>
        <span>Configured</span>
      </div>
    );
  else return <Loading className={clsx(tableClassNames.loading)} />;
};

export const Companies = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<TCompanyStatuses>(TCompanyStatuses.Active);
  const [companyNameFilter, setCompanyNameFilter] = useState<string>();

  const { data: companies, loading, error } = useQueryListAllCompaniesOLD();

  const getRowId = useCallback((company: TListAllCompaniesFieldsFragment) => {
    return company.id;
  }, []);

  const onRowClick: TOnRowClick<TListAllCompaniesFieldsFragment> = useCallback(
    (row) => {
      navigate(generatePath(`${COMPANY_PATH}`, { companyId: row.id }));
    },
    [navigate]
  );

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      type: 'singleSelect',
      label: 'Status',
      name: 'status',
      onChange: (v) => {
        setStatusFilter(v != null ? (v as TCompanyStatuses) : TCompanyStatuses.Active);
      },
      options: [
        {
          label: 'Active',
          value: TCompanyStatuses.Active
        },
        {
          label: 'Inactive',
          value: TCompanyStatuses.Inactive
        },
        {
          label: 'Cancelled',
          value: TCompanyStatuses.Canceled
        },
        {
          label: 'Deleted',
          value: TCompanyStatuses.Deleted
        }
      ],
      value: statusFilter
    });

    filtersList.push({
      type: 'textInput',
      label: 'Company Name',
      name: 'companyName',
      onChange: (v) => {
        setCompanyNameFilter(v != null ? (v as string) : undefined);
      },
      value: companyNameFilter
    });

    return filtersList;
  }, [statusFilter, companyNameFilter]);

  const filteredCompanies = useMemo(() => {
    if (!companies) return [];
    return companies.filter((s) => {
      const hasReachedEndDate = s.endDate && readableDate(s.endDate, 'yyyy-MM-dd') < readableDate(new Date(), 'yyyy-MM-dd');
      if (companyNameFilter != null && companyNameFilter !== '' && (s.name == null || quickScore(s.name, companyNameFilter) < 0.7)) return false;

      if (statusFilter !== null && statusFilter !== undefined) {
        if (statusFilter === TCompanyStatuses.Active) return s.active && s.isLive && !hasReachedEndDate;
        if (statusFilter === TCompanyStatuses.Inactive) return s.active && !s.isLive;
        if (statusFilter === TCompanyStatuses.Canceled) return hasReachedEndDate && s.active && s.isLive;
        if (statusFilter === TCompanyStatuses.Deleted) return hasReachedEndDate && !s.active && !s.isLive;
      }

      return true;
    });
  }, [companies, statusFilter, companyNameFilter]);

  //console.log('companies?:', companies);
  console.log('filteredCompanies?:', filteredCompanies);
  //console.log('{id, name}:', filteredCompanies.map(({ id, name }) => ({ id, name })));

  if (loading) return <LoadingPage />;

  return (
    <TablePage error={error} title='Companies'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TListAllCompaniesFieldsFragment>
          enableSorting={true}
          defaultSorting={{ id: 'name', desc: false }}
          className={clsx(filteredCompanies.length === 0 && tableStyles.tableHeader)}
          data={filteredCompanies}
          emptyText={'No companies match this criteria.'}
          hoverableRows={true}
          fixedHeader={true}
          onRowClick={onRowClick}
          getRowId={getRowId}
          virtual={false}
          columns={[
            {
              header: 'Employee Count',
              accessorKey: 'employeeCount',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{cell.row.original.employeeCount}</span>
                  </div>
                );
              }
            },
            {
              header: 'Name',
              accessorKey: 'name',
              cell: ({ cell }) => {
                const { name } = cell.row.original;
                return (
                  <div className={clsx(tableClassNames.primary)}>
                    <span>{name}</span>
                  </div>
                );
              }
            },
            {
              header: 'Delivery Method',
              accessorKey: 'deliveryMethod',
              cell: ({ cell }) => {
                const { deliveryMethod } = cell.row.original;
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{deliveryMethod}</span>
                  </div>
                );
              }
            },
            {
              header: 'Configuration Done',
              accessorKey: 'fullyConfigured',
              cell: ConfiguredCell
            },
            {
              header: 'Last Roster Upload',
              accessorKey: 'lastRosterUpload',
              cell: ({ cell }) => {
                const { lastRosterUpload } = cell.row.original;
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{lastRosterUpload ? readableDate(lastRosterUpload, 'do MMM, yyyy HH:mm') : '-'}</span>
                  </div>
                );
              }
            },
            {
              header: 'Manager',
              accessorKey: 'accountManager',
              cell: ({ cell }) => {
                const { accountManager } = cell.row.original;
                const [managerName] = accountManager ? accountManager.split(' (') : ['-'];
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{managerName} </span>
                  </div>
                );
              }
            }
          ]}
        />
      </div>
    </TablePage>
  );
};
